import type {
  ProofreadingBatchCheckOptions,
  ResolvedProofreadingBatchChecks,
} from "@/lib/api/proofreadingAi";
import {
  AI_PROOFREADING_CHECK_IDS,
  AI_PROOFREADING_FISH_PER_CHAR,
  isAiProofreadingCheckSelectable,
  type AiProofreadingCheckId,
} from "@/constants/proofreadingAiChecks";

const EMPTY_CHECKS: ResolvedProofreadingBatchChecks = {
  errorCheck: false,
  nounCheck: false,
  storyCheck: false,
  expressionCheck: false,
  foreshadowCheck: false,
};

function checksWithOnly(checkId: AiProofreadingCheckId): ResolvedProofreadingBatchChecks {
  return {
    ...EMPTY_CHECKS,
    [checkId]: true,
  };
}

/**
 * 校正項目は同時に1種だけ。初期状態は未選択。
 * 複数 ON なら一覧の先頭を残す。どれも ON でなければ未選択のまま。
 * 旧 creativeCheck だけが ON のときは物語・設定に寄せる。
 */
export function resolveAiProofreadingChecks(
  flags?: ProofreadingBatchCheckOptions,
): ResolvedProofreadingBatchChecks {
  if (!flags) {
    return { ...EMPTY_CHECKS };
  }

  const normalized: ProofreadingBatchCheckOptions = { ...flags };
  if (
    flags.creativeCheck === true
    && flags.storyCheck === undefined
    && flags.expressionCheck === undefined
  ) {
    normalized.storyCheck = true;
  }

  const selected = AI_PROOFREADING_CHECK_IDS.find(
    (checkId) => normalized[checkId] === true && isAiProofreadingCheckSelectable(checkId),
  );
  if (!selected) {
    return { ...EMPTY_CHECKS };
  }
  return checksWithOnly(selected);
}

/** API 投入用: 固有名詞チェックと未リリース項目を常に OFF にする。 */
export function sanitizeAiChecksForSubmit(
  checks: ResolvedProofreadingBatchChecks,
): ResolvedProofreadingBatchChecks {
  return {
    ...checks,
    nounCheck: false,
    foreshadowCheck: false,
  };
}

/** Fish 課金対象（noun 除外）。 */
export function countEnabledAiProofreadingChecks(
  flags: ResolvedProofreadingBatchChecks,
): number {
  let count = 0;
  if (flags.errorCheck) count += 1;
  if (flags.storyCheck) count += 1;
  if (flags.expressionCheck) count += 1;
  if (flags.foreshadowCheck) count += 1;
  return count;
}

/** 原稿 1 文字 = 5 fish。項目は1種だけなのでチェック数では掛けない。 */
export function calcAiProofreadingFishEstimate(
  totalChars: number,
  enabledCheckCount: number,
): number {
  if (totalChars <= 0 || enabledCheckCount <= 0) return 0;
  return totalChars * AI_PROOFREADING_FISH_PER_CHAR;
}
